import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/** Keep a problem's row/card compact even when the API returns many topics. */
const MAX_VISIBLE_TOPICS = 6;

interface TopicChipsProps {
  topics?: string[] | null;
  /** Classes for the wrapping row. */
  className?: string;
  /** Classes for the collapsed "Topics" trigger. */
  triggerClassName?: string;
  /** Classes for each revealed topic pill. */
  topicClassName?: string;
}

/** Collapsed-by-default topic label: users see "Topics" and only the real
 *  topics (array, two pointers, ...) after they click. */
export function TopicChips({ topics, className = '', triggerClassName = '', topicClassName = '' }: TopicChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const list = topics ?? [];

  if (list.length === 0) return null;

  const visible = expanded ? list.slice(0, MAX_VISIBLE_TOPICS) : [];
  const overflow = list.length - MAX_VISIBLE_TOPICS;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Cards/rows wrap this chip; the toggle must not navigate into the problem.
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  return (
    <span
      className={`flex flex-wrap items-center gap-1 ${className}`}
      onClick={(event) => event.stopPropagation()}
    >
      <span
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`inline-flex cursor-pointer select-none items-center gap-1 ${triggerClassName}`}
      >
        Topics
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </span>
      {expanded &&
        visible.map((topic, index) => (
          <span key={`${topic}-${index}`} className={topicClassName}>
            {topic}
          </span>
        ))}
      {expanded && overflow > 0 && <span className={topicClassName}>+{overflow}</span>}
    </span>
  );
}

export default TopicChips;
